import { useMemo } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { emit } from '@/lib/events';
import type { Selection, SelectionLevel } from './types';
import { EMPTY_SELECTION } from './types';

const STORAGE_KEY = 'hitmap_selection_v2';

interface PlacesState {
  selection: Selection;
  toggle: (level: SelectionLevel, code: string) => void;
  clear: () => void;
  clearLevel: (level: SelectionLevel) => void;
}

// Map level → key in Selection (irregular plural for "comune" → "comuni")
const LEVEL_KEY: Record<SelectionLevel, keyof Selection> = {
  region: 'regions',
  province: 'provinces',
  comune: 'comuni',
};

export const usePlacesStore = create<PlacesState>()(
  persist(
    (set, get) => ({
      selection: EMPTY_SELECTION,
      toggle: (level, code) => {
        const sel = get().selection;
        const key = LEVEL_KEY[level];
        const list = sel[key];
        const next: Selection = {
          ...sel,
          [key]: list.includes(code) ? list.filter((c) => c !== code) : [...list, code],
        };
        set({ selection: next });
        const total = next.regions.length + next.provinces.length + next.comuni.length;
        emit('place:selected', { level, total_selected: total });
      },
      clear: () => set({ selection: EMPTY_SELECTION }),
      clearLevel: (level) => {
        set({ selection: { ...get().selection, [LEVEL_KEY[level]]: [] } });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ selection: state.selection }),
    },
  ),
);

export function useSelectedSets() {
  const sel = usePlacesStore((s) => s.selection);
  // Memoize derived Sets — иначе SelectionOverlay/BeaconPins useMemo зависят от
  // нестабильных ref и пересчитывают тяжёлые операции на каждом render.
  return useMemo(
    () => ({
      regions: new Set(sel.regions),
      provinces: new Set(sel.provinces),
      comuni: new Set(sel.comuni),
      total: sel.regions.length + sel.provinces.length + sel.comuni.length,
    }),
    [sel.regions, sel.provinces, sel.comuni],
  );
}
